import {
  Handle,
  Position,
  NodeResizer,
  NodeToolbar,
  useReactFlow,
  useNodeId,
} from '@xyflow/react';
import React, { useCallback, useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
const DescriptionNode = ({ data, selected }) => {
  const id = useNodeId();
  const { setNodes } = useReactFlow();

  // local buffer states
  const [label, setLabel] = useState(data.label ?? '');
  const [desc, setDesc] = useState(data.description ?? '');
  const [showDesc, setShowDesc] = useState(
    data.showDesc !== undefined ? data.showDesc : true
  );
  const [bgColor, setBgColor] = useState(data.bgColor ?? '#ffffff');
  const [fontColor, setFontColor] = useState(data.fontColor ?? '#111111');
  const [fontSize, setFontSize] = useState(data.fontSize ?? 14);
  // keep sync with external changes
  useEffect(() => setLabel(data.label ?? ''), [data.label]);
  useEffect(() => setDesc(data.description ?? ''), [data.description]);
  useEffect(
    () => setShowDesc(data.showDesc !== undefined ? data.showDesc : true),
    [data.showDesc]
  );

  // if you want buffered local states for these too:
  useEffect(() => setBgColor(data.bgColor ?? '#ffffff'), [data.bgColor]);
  useEffect(() => setFontColor(data.fontColor ?? '#111111'), [data.fontColor]);
  useEffect(() => setFontSize(data.fontSize ?? 14), [data.fontSize]);

  const handleResize = useCallback(
    (_evt, params) => {
      if (!id) return;
      const { width, height } = params || {};
      if (width == null && height == null) return;

      setNodes((ns) =>
        ns.map((n) =>
          n.id === id
            ? {
                ...n,
                style: {
                  ...(n.style || {}),
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
                data: {
                  ...n.data,
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  const commit = useCallback(
    (patch) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, ...patch } } : n
        )
      );
    },
    [id, setNodes]
  );

  const stopKeys = (e) => e.stopPropagation();

  return (
    <div
      className="custom-node"
      style={{
        position: 'relative',
        background: data.bgColor ?? '#ffffff',
        color: data.fontColor ?? '#111111',
        fontSize: (data.fontSize ?? 14) + 'px',
        padding: 12,
        width: data.width ?? 'auto',
        height: data.height ?? 'auto',
      }}
    >
      {/* === Toolbar toggle === */}
      <NodeToolbar
        isVisible={selected}
        position={Position.Top}
        className="rounded-xl border bg-white p-1 shadow z-50 flex gap-2"
      >
        {/* Toggle description */}
        <button
          className="px-2 py-1 text-xs rounded border text-black"
          onClick={() => {
            setShowDesc((v) => {
              const newVal = !v;
              commit({ showDesc: newVal });
              return newVal;
            });
          }}
        >
          {showDesc ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Quick color picker */}
        <input
          type="color"
          className="w-6 h-6 rounded border cursor-pointer"
          value={bgColor}
          onInput={(e) => setBgColor(e.target.value)} 
          onChange={() => commit({ bgColor: bgColor })} 
          title="Change fill color"
        />

        {/* Increase font size */}
        <button
          className="px-2 py-1 text-xs rounded border text-black"
          onClick={() =>
            commit({
              fontSize: Math.min((data.fontSize ?? 14) + 2, 48),
            })
          }
          title="Increase font size"
        >
          A+
        </button>

        {/* Decrease font size */}
        <button
          className="px-2 py-1 text-xs rounded border text-black"
          onClick={() =>
            commit({
              fontSize: Math.max((data.fontSize ?? 14) - 2, 10),
            })
          }
          title="Decrease font size"
        >
          A-
        </button>

        {/* Delete node */}
        <button
          className="px-2 py-1 text-xs rounded border text-red-600"
          onClick={() => {
            if (!id) return;
            setNodes((ns) => ns.filter((n) => n.id !== id));
          }}
          title="Delete node"
        >
          ✕
        </button>
      </NodeToolbar>
      <NodeResizer
        isVisible={selected}
        minWidth={100}
        minHeight={80}
        onResizeEnd={handleResize}
        onResize={handleResize}
      />
      {/* handles (you can keep your originals) */}
      <Handle
        type="source"
        position={Position.Left}
        id="1"
        className="custom-handle-source"
        style={{ left: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="target"
        position={Position.Left}
        id="1"
        className="custom-handle-target"
        style={{ left: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="source"
        position={Position.Right}
        id="2"
        className="custom-handle-source"
        style={{ right: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="target"
        position={Position.Right}
        id="2"
        className="custom-handle-target"
        style={{ right: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="source"
        position={Position.Top}
        id="4"
        className="custom-handle-source"
        style={{ top: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="target"
        position={Position.Top}
        id="4"
        className="custom-handle-target"
        style={{ top: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="source"
        position={Position.Bottom}
        id="3"
        className="custom-handle-source"
        style={{ bottom: -6, visibility: selected ? 'visible' : 'hidden' }}
      />{' '}
      <Handle
        type="target"
        position={Position.Bottom}
        id="3"
        className="custom-handle-target"
        style={{ bottom: -6, visibility: selected ? 'visible' : 'hidden' }}
      />
      {/* label always visible */}
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => commit({ label })}
        onKeyDown={stopKeys}
        className="node-label"
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          color: data.fontColor ?? '#111111',
          fontSize: (data.fontSize ?? 14) + 'px',
          textAlign: 'center',
        }}
      />
      {/* description only if toggled on */}
      {showDesc && (
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => commit({ description: desc })}
          onKeyDown={stopKeys}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            color: data.fontColor ?? '#352a2a',
            fontSize: (data.fontSize ?? 14) - 2 + 'px',
            resize: 'none',
            minHeight: '40px',
            textAlign: 'center',
            marginTop: 6,
          }}
        />
      )}
    </div>
  );
};

export default DescriptionNode;
